import { useAppSelector } from 'dux/app/hooks';

import { availableCountries, countriesCode } from 'constants/countries';
import { productsCategories } from 'constants/products';

import {
  haircareTrialOffer,
  referAFriend,
  skincareMinis,
  skincareTrialOffer,
} from 'assets/content/postPurchase/promo';

import { createFlatTranslationFn } from 'utils/createTranslationFn';
import logSentryError from 'utils/logSentry';

import { getUserGeolocationGuessedCountry } from 'dux/user/selectors';
import {
  type ExcludeByPromoType,
  excludePromo,
  PromoIdentifiers,
  type Promotion,
  usePromoIdentifiers,
} from 'dux/promotions/hooks';

import type { Content, ProductsCategories } from './PostPurchaseSuccessV2';

type CountryCode = (typeof availableCountries)[number];

export const usePostPurchaseOfferContent = (
  promotionOverride?: ExcludeByPromoType<Promotion, 'feedback'>,
  orderCategory?: ProductsCategories,
): Content => {
  const country: CountryCode = useAppSelector(getUserGeolocationGuessedCountry);
  const { availablePromoList } = usePromoIdentifiers('post-purchase-success');
  const promo = promotionOverride ?? excludePromo(availablePromoList, 'feedback')[0];

  switch (promo?.id) {
    case PromoIdentifiers.SKINCARE_MINIS: {
      const t = createFlatTranslationFn(skincareMinis, country, 'US');
      return {
        tracking: {
          pageView: t('tracking.pageView'),
        },
        title: t('title'),
        description: t('description'),
        benefits: null,
        cta: {
          label: t(`cta.${promo.context.consultationStatus}.label`),
          path: t(`cta.${promo.context.consultationStatus}.path`),
          dataClick: t(`cta.${promo.context.consultationStatus}.dataClick`),
          dataFrom: t(`cta.${promo.context.consultationStatus}.dataFrom`),
        },
        image: {
          desktop: t('image.desktop'),
          mobile: t('image.mobile'),
        },
        badge: t('badge'),
      };
    }
    case PromoIdentifiers.TRIAL_OFFER:
      switch (promo.context.contentCategory) {
        case 'haircare': {
          if (orderCategory !== productsCategories.HAIRCARE) {
            const t = createFlatTranslationFn(haircareTrialOffer, country, countriesCode.US);
            return {
              tracking: {
                pageView: t('tracking.pageView'),
              },
              title: t('title'),
              description: null,
              benefits: t('benefits'),
              cta: {
                label: t(`cta.${promo.context.consultationStatus}.label`),
                path: t(`cta.${promo.context.consultationStatus}.path`),
                dataClick: t(`cta.${promo.context.consultationStatus}.dataClick`),
                dataFrom: t(`cta.${promo.context.consultationStatus}.dataFrom`),
              },
              image: {
                desktop: t('image.desktop'),
                mobile: t('image.mobile'),
              },
              badge: t('badge'),
            };
          }
          break;
        }
        case 'skincare': {
          if (orderCategory !== productsCategories.SKINCARE) {
            const t = createFlatTranslationFn(skincareTrialOffer, country, countriesCode.US);
            return {
              tracking: {
                pageView: t('tracking.pageView'),
              },
              title: t('title'),
              description: null,
              benefits: t('benefits'),
              cta: {
                label: t(`cta.${promo.context.consultationStatus}.label`),
                path: t(`cta.${promo.context.consultationStatus}.path`),
                dataClick: t(`cta.${promo.context.consultationStatus}.dataClick`),
                dataFrom: t(`cta.${promo.context.consultationStatus}.dataFrom`),
              },
              image: {
                desktop: t('image.desktop'),
                mobile: t('image.mobile'),
              },
              badge: t('badge'),
            };
          }
          break;
        }
        case 'both':
          break;
        case 'disabled':
          break;
        default: {
          const exhaustiveCheck: never = promo.context;
          return exhaustiveCheck;
        }
      }
      break;
    case PromoIdentifiers.REFER_A_FRIEND: {
      const t = createFlatTranslationFn(referAFriend, country, countriesCode.US);
      return {
        tracking: {
          pageView: t('tracking.pageView'),
        },
        title: t('title'),
        description: t('description'),
        benefits: null,
        cta: {
          path: t(`cta.${promo.context.contentCategory}.path`),
          label: t(`cta.${promo.context.contentCategory}.label`),
          dataClick: t(`cta.${promo.context.contentCategory}.dataClick`),
          dataFrom: t(`cta.${promo.context.contentCategory}.dataFrom`),
        },
        image: {
          desktop: t('image.desktop'),
          mobile: t('image.mobile'),
        },
        badge: t('badge'),
      };
    }
    default: {
      logSentryError('[PostPurchaseSuccessV2]', 'impossible promotion state', {
        logLevel: 'warning',
      });
    }
  }
  const t = createFlatTranslationFn(referAFriend, country, countriesCode.US);
  return {
    tracking: {
      pageView: t('tracking.pageView'),
    },
    title: t('title'),
    description: t('description'),
    benefits: null,
    cta: {
      path: t('cta.default.path'),
      label: t('cta.default.label'),
      dataClick: t(`cta.default.dataClick`),
      dataFrom: t(`cta.default.dataFrom`),
    },
    image: {
      desktop: t('image.desktop'),
      mobile: t('image.mobile'),
    },
    badge: t('badge'),
  };
};
